import React, {useEffect, useState} from 'react'
import {BucketInfo} from '../../../DTO/BucketInfoDTO'
import {PaymentMethod} from '../../../DTO/PaymentMethodDTO'
import {ContractInputInfo, ContractMethod, UsingMyCreditCardInfo} from '../../../Props/ContractInputInfo'
import {ContractInfo} from '../../../DTO/MemberContractInfoDTO'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import H from 'history'
import {useLocation} from 'react-router-dom'
import {Nullable, Optional} from '../../../../Common/TypeHelper'
import {TokenResponse, useGmo} from '../../Hooks/UseGmo'
import {MBMemberError} from '../../../Common/MBMemberError'
import {ContractTermConfirmContent} from '../../Components/PaymentsRelations/ConfirmContentBlocks/ContractTermConfirmContent'
import {ContractPaymentMethodConfirm} from '../../Components/PaymentsRelations/ConfirmContentBlocks/ContractPaymentMethodConfirm'
import {BucketContractInputInfoCreditCard, BucketContractInputInfoMobileCarrier} from '../../../DTO/MemberJoinBucketDTO'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {CommonResponseDTO} from '../../../../Network/CommonResponse'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {MembersMobileCarrierMaintenancePage} from '../Error/MembersMobileCarrierMaintenancePage'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {checkResponseAndGetDataOrError, isError} from '../../../Utils/MemberResponseStatusCheck'
import {MobileCarrierStartPaymentInfoDTO} from '../../../DTO/MobileCarrierStartPaymentInfoDTO'
import {BucketEditContractInputInfoConvenienceStore, MemberEditContractDTO} from '../../../DTO/MemberEditContractDTO'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'

type Props = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

export type EditContractConfirmPageLocationState = {
  bucketInfo: BucketInfo
  paymentMethodList: PaymentMethod[]
  contractInputInfo: ContractInputInfo
  myCreditCardInfoList: UsingMyCreditCardInfo[]
  nextContractInfo: ContractInfo
}

export const MembersEditContractConfirmPage = (props: Props): Nullable<JSX.Element> => {
  const scriptUrl: Optional<string> = process.env.REACT_APP_MEMBUCKET_ADMIN_APP_GMO_SCRIPT_URL
  const timeout: Optional<number> =
    parseInt(process.env.REACT_APP_MEMBUCKET_ADMIN_APP_GMO_SCRIPT_TIMEOUT ?? '5000', 10)

  const [tokenResponse, setTokenResponse] = useState<TokenResponse>()

  const location: H.Location<EditContractConfirmPageLocationState> =
    useLocation<EditContractConfirmPageLocationState>()
  const locationState: EditContractConfirmPageLocationState = location.state

  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [processing, setProcessing] = useState<boolean>(false)
  const pageTitle = 'お支払いに関する情報変更確認'

  const throwMBMemberError = useMBMemberError()

  if (!scriptUrl) {
    throw new MBMemberError('SystemError')
  }
  const [loading, error, gmo] = useGmo(scriptUrl)

  const [gmoTimeoutErrorFlg, setGmoTimeoutErrorFlg] = useState(false)
  const [mobileCarrierMaintenance, setMobileCarrierMaintenance] = useState<boolean>(false)

  useEffect(() => {
    if (!locationState?.contractInputInfo) {
      return
    }
    if (locationState.contractInputInfo.creditCard?.creditCardType === 'myCard') {
      return
    }

    if (gmo) {
      if (locationState.contractInputInfo.method !== 'credit_card') {
        return
      }
      if (!locationState.contractInputInfo.creditCard) {
        return
      }

      const shopCode = locationState.bucketInfo.shopId
      if (!shopCode) {
        throw new MBMemberError('SystemError')
      }

      const {cardNumber, expireMonth, expireYear, holderName, securityCode} =
        locationState.contractInputInfo.creditCard.newCreditCardInfo

      gmo.init(shopCode)

      const timeoutId = setTimeout(() => {
        setGmoTimeoutErrorFlg(true)
      }, timeout)

      gmo.getToken({
        tokennumber: 2,
        cardno: cardNumber,
        expire: expireYear.slice(-2) + expireMonth,
        securitycode: securityCode,
        holdername: holderName,
      }, (result: TokenResponse) => {
        clearTimeout(timeoutId)
        setTokenResponse(result)
      })
    }
  }, [gmo])

  if (isInvalidLocationState(locationState)) {
    throw new MBMemberError('InvalidOperation')
  }

  if (locationState.contractInputInfo.method === 'credit_card'
    && locationState.contractInputInfo.creditCard?.creditCardType === 'newCard') {
    if (gmoTimeoutErrorFlg) {
      throw new MBMemberError('SystemError')
    }

    if (error) {
      throw new MBMemberError('SystemError')
    }
    if (loading) {
      return null
    }
    if (!gmo) {
      return null
    }

    if (!tokenResponse) {
      return null
    }
  }

  function isInvalidLocationState(locationState: EditContractConfirmPageLocationState): boolean {
    if (!locationState) {
      return true
    }
    if (!locationState.bucketInfo) {
      return true
    }
    if (!locationState.myCreditCardInfoList) {
      return true
    }
    if (!locationState.paymentMethodList) {
      return true
    }
    if (!locationState.contractInputInfo) {
      return true
    }
    if (!locationState.contractInputInfo.method) {
      return true
    }
    // noinspection RedundantIfStatementJS
    if (!locationState.nextContractInfo) {
      return true
    }

    return false
  }

  const {
    bucketInfo,
    contractInputInfo,
    myCreditCardInfoList,
    nextContractInfo,
  } = locationState

  const {creditCard} = contractInputInfo

  const goToEditContractCompletePage = async (): Promise<void> => {
    setProcessing(true)

    try {
      if (!contractInputInfo.method) {
        // noinspection ExceptionCaughtLocallyJS
        throw new MBMemberError('InvalidOperation')
      }
      const method: ContractMethod = contractInputInfo.method

      const generateBucketContractInputInfo = ():
        BucketContractInputInfoCreditCard |
        BucketEditContractInputInfoConvenienceStore |
        BucketContractInputInfoMobileCarrier => {
        const selectedMyCreditCardInfo =
          creditCard?.myCreditCardInfo.securityCodeMap
            .find(item => item.cardId === creditCard.myCreditCardInfo.selectedCardId)

        switch (method) {
          case 'credit_card':
            if (creditCard.creditCardType === 'newCard') {
              return {
                contractTerm: contractInputInfo.term,
                paymentMethod: 'credit_card',
                tokenList: tokenResponse?.tokenObject.token || [],
                creditCardExpireDate:
                  `${creditCard.newCreditCardInfo.expireMonth}/${creditCard.newCreditCardInfo.expireYear}`,
                cardId: undefined,
                securityCode: undefined,
              }
            }

            return {
              contractTerm: contractInputInfo.term,
              paymentMethod: 'credit_card',
              tokenList: [],
              creditCardExpireDate: undefined,
              cardId: creditCard?.myCreditCardInfo.selectedCardId,
              securityCode: selectedMyCreditCardInfo?.securityCode,
            }
          case 'mobile_carrier':
            return {
              contractTerm: contractInputInfo.term,
              paymentMethod: 'mobile_carrier',
              paymentMethodDetail: contractInputInfo.mobileCarrier.carrierKey,
            }
          case 'convenience_store':
            return {
              contractTerm: contractInputInfo.term,
              paymentMethod: 'convenience_store',
            }
          default:
            throw new MBMemberError('InvalidOperation')
        }
      }

      const putBucketContractInfoResult:
        CommonResponseDTO<Nullable<MobileCarrierStartPaymentInfoDTO>> =
        await props.memberRepo.putBucketContractInfo(new MemberEditContractDTO(
          bucketInfo.key,
          generateBucketContractInputInfo(),
        ))
      const validatePutBucketContractInfoResult:
        Nullable<MobileCarrierStartPaymentInfoDTO> | MBMemberError
        = checkResponseAndGetDataOrError(putBucketContractInfoResult)

      const changeContractByCreditCardOrConvenienceStore = (): void => {
        if (isError(validatePutBucketContractInfoResult)) {
          if (validatePutBucketContractInfoResult.message === 'OvertimeError') {
            setMobileCarrierMaintenance(true)
            return
          }
          if (validatePutBucketContractInfoResult.message === 'InvalidCreditCard') {
            props.navigator.backToEditContractInputPageCauseCreditCardError(
              locationState.bucketInfo,
              locationState.paymentMethodList,
              locationState.myCreditCardInfoList,
              locationState.contractInputInfo,
              locationState.nextContractInfo,
            )
            return
          }
          if (validatePutBucketContractInfoResult.message === 'NotFound') {
            throw new MBMemberError('SystemError')
          }
          throw validatePutBucketContractInfoResult
        }

        if (method === 'convenience_store') {
          props.navigator.goToEditContractCompletePage(bucketInfo, nextContractInfo.startDate)
          return
        }

        props.navigator.goToEditContractCompletePage(bucketInfo)
      }
      if (method === 'credit_card' || method === 'convenience_store') {
        changeContractByCreditCardOrConvenienceStore()
        return
      }

      const changeContractByMobileCarrier = (): void => {
        if (isError(validatePutBucketContractInfoResult)) {
          if (validatePutBucketContractInfoResult.message === 'BadRequest') {
            setErrorMessages(
              validatePutBucketContractInfoResult.errorMessageList ?? ['リクエストが不正です。'],
            )
            setProcessing(false)
            return
          }
          if (validatePutBucketContractInfoResult.message === 'OvertimeError') {
            setMobileCarrierMaintenance(true)
            return
          }
          throw validatePutBucketContractInfoResult
        }

        if (!putBucketContractInfoResult?.data) {
          throw new MBMemberError('ServerError')
        }
        props.navigator.goToEditContractMobileCarrierTransitionPage(
          {...putBucketContractInfoResult.data},
        )
      }
      if (method === 'mobile_carrier') {
        changeContractByMobileCarrier()
        return
      }

      // 期待しない支払い方法の場合はエラー
      // noinspection ExceptionCaughtLocallyJS
      throw new MBMemberError('InvalidOperation')

    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }

  const determineButtonLabel = locationState.contractInputInfo.method === 'mobile_carrier' ?
    '決済に進む' : '確定する'

  return (
    mobileCarrierMaintenance
      ? <MembersMobileCarrierMaintenancePage
        pageTitle={pageTitle}
        operationName={'お支払いに関する情報変更手続き'}
        bucketInfo={bucketInfo}
        buttonName={bucketInfo?.fcMyPageInfo.name}
        url={bucketInfo?.fcMyPageInfo.url}
      />
      :
      <MembersPageTemplate
        hideFooterNavi={true}
        bucketInfo={bucketInfo}
      >

        <PageTitle>{pageTitle}</PageTitle>

        <StepGuide activity={'edit'} current={'confirm'}/>

        <ContractTermConfirmContent
          title={'次回契約単位'}
          term={contractInputInfo.term}
          contractMethod={contractInputInfo.method}
          nextContractStartDate={nextContractInfo.startDate}
        />

        <ContractPaymentMethodConfirm
          title={'次回契約のお支払い方法'}
          amount={contractInputInfo.amount}
          method={contractInputInfo.method}
          methodDetail={{
            paymentInputDetailWithMethod: contractInputInfo,
            myCreditCardInfoList: myCreditCardInfoList,
            maskedCardNo: tokenResponse?.tokenObject.maskedCardNo,
          }}
        />

        <MessageForAction>
          上記の内容に間違いがなければ<br/>『{determineButtonLabel}』ボタンを押してください。
        </MessageForAction>

        <PIMStack alignItems={'center'}>
          <ErrorDisplay errors={errorMessages}/>
        </PIMStack>

        <PIMPageActions>
          <PIMSubmitButton
            testId={'determine-button'}
            disabled={processing}
            onClick={(): Promise<void> => goToEditContractCompletePage()}
          >
            {determineButtonLabel}
          </PIMSubmitButton>
          <PIMSecondaryButton onClick={(): void => props.navigator.goBack()}>
            お支払いに関する情報変更に戻る
          </PIMSecondaryButton>
        </PIMPageActions>
      </MembersPageTemplate>
  )
}
